import React, { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import { useQuery as useApolloQuery } from "@apollo/client/react";
import ChargesRow from "src/components/billing/ChargesRow";
import OneTimeChargesModal from "src/components/project/inbound/OneTimeChargesModal";
import CategoryChargesInfoModal from "src/components/billing/CategoryChargesInfoModal";
import InventoryRequestCategoryChargesEditModal from "src/components/billing/InventoryRequestCategoryChargesEditModal";
import BillingQuantitiesModal from "src/components/capture/ReceivingWizard/Shipment/Billing/BillingQuantitiesModal";
import { FETCH_PROJECT_QUERY } from "src/constants/graphql/queries";
import { UPDATE_INVENTORY_REQUEST_MUTATION } from "src/constants/graphql/mutations";
import useMutation from "src/hooks/useMutation";
import useNotification from "src/hooks/notification";
import useAuth from "src/hooks/useAuth";
import { getOutboundBillingType } from "src/utils/billingHelpers";
import OutboundChargesHeader from "./OutboundChargesHeader";

const chargeTitles = {
  outboundCharges: "Outbound Charges",
  laborCharges: "Labor Charges",
  materialCharges: "Material Charges",
  equipmentCharges: "Equipment Charges",
};

export default function OutboundChargesSummaryModal({
  inventoryRequest,
  onClose,
  isSimpleCheckoutContractWarehousesMode = false,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isInternalStaff = ["admin", "manager", "inventory"].includes(user?.role);
  const queryParams = new URLSearchParams(location.search);
  const billing = inventoryRequest?.billingCharges || {};
  const projectId = inventoryRequest?.projectId;
  const { data: { project } = {} } = useApolloQuery(FETCH_PROJECT_QUERY, {
    variables: { id: projectId },
    skip: !projectId,
  });

  const [openOneTime, setOpenOneTime] = useState(false);
  const openChargesInfoModal = queryParams.get("openChargesInfoModal");
  const openBillingQuantitiesModal = queryParams.get("openBillingQuantitiesModal") === "outbound";

  const toggleChargesInfoModal = useCallback(
    value => {
      const params = new URLSearchParams(location.search);
      if (value) {
        params.set("openChargesInfoModal", value);
      } else {
        params.delete("openChargesInfoModal");
      }
      navigate({ search: params.toString() });
    },
    [navigate, location.search]
  );

  // Swaps the quantities modal and charges info modal in one navigation;
  // two navigations built from the same location.search would undo each other.
  const toggleBillingQuantitiesModal = useCallback(
    (isOpen, chargesInfoModal = null) => {
      const params = new URLSearchParams(location.search);
      if (isOpen) {
        params.set("openBillingQuantitiesModal", "outbound");
      } else {
        params.delete("openBillingQuantitiesModal");
      }
      if (chargesInfoModal) {
        params.set("openChargesInfoModal", chargesInfoModal);
      } else {
        params.delete("openChargesInfoModal");
      }
      navigate({ search: params.toString() });
    },
    [location.search, navigate]
  );

  const [openChargesEditModal, setOpenChargesEditModal] = useState(null);
  const [updateInventoryRequest, { loading }] = useMutation(UPDATE_INVENTORY_REQUEST_MUTATION);
  const { notifySuccess, notifyError } = useNotification();

  const outboundBillingType = useMemo(() => getOutboundBillingType(project), [project]);

  // Until the project loads, open via the URL param; the modal renders once
  // the billing type resolves.
  const handleOpenBillingQuantities = useCallback(() => {
    if (project && !outboundBillingType) {
      notifyError("No editable billing type configured for this order");
      return;
    }
    toggleBillingQuantitiesModal(true);
  }, [project, outboundBillingType, notifyError, toggleBillingQuantitiesModal]);

  const toNumber = value => (typeof value === "number" && !Number.isNaN(value) ? value : 0);

  const getViewOrderDetailsTo = () => {
    const returnTo = `${location.pathname}${location.search}`;
    const params = new URLSearchParams(location.search);
    params.delete("outboundChargesSummaryId");
    params.set("inventoryRequestId", inventoryRequest.id);
    params.set("returnTo", returnTo);
    return { pathname: location.pathname, search: params.toString() };
  };

  const handleSubmit = useCallback(
    async values => {
      try {
        const result = await updateInventoryRequest({
          variables: {
            id: inventoryRequest.id,
            input: {
              oneTimeCharges: values,
            },
          },
        });

        if (result.data?.updateInventoryRequest?.errors?.length > 0) {
          const errorMessage = result.data.updateInventoryRequest.errors[0].message;
          notifyError(errorMessage);
        } else {
          notifySuccess("One-time charges saved successfully");
          setOpenOneTime(false);
        }
      } catch (err) {
        notifyError("Failed to save charges");
      }
    },
    [inventoryRequest.id, updateInventoryRequest, notifyError, notifySuccess]
  );

  const charges = {
    outboundCharges: toNumber(billing.outbound_total ?? billing.base_total),
    laborCharges: toNumber(billing.labor_total),
    materialCharges: toNumber(billing.material_total),
    equipmentCharges: toNumber(billing.equipment_total),
    oneTimeCharges: toNumber(billing.one_time_total),
    salesTax: toNumber(inventoryRequest?.salesTax?.total),
    billingCharges: toNumber(billing.grand_total),
  };

  return (
    <>
      <Dialog
        open
        onClose={onClose}
        maxWidth={false}
        sx={{ "& .MuiDialog-paper": { maxWidth: "440px", width: "100%" } }}
        aria-labelledby="outbound-charges-summary-title"
      >
        <DialogTitle id="outbound-charges-summary-title">
          <Typography fontSize={20} fontWeight={500} color="text.primary" align="center">
            Outbound Charges Summary
          </Typography>
          <IconButton
            onClick={onClose}
            size="large"
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          <OutboundChargesHeader inventoryRequest={inventoryRequest} />
          <Box display="flex" flexDirection="column" gap={1} mx="auto" mt={3}>
            {!isSimpleCheckoutContractWarehousesMode && (
              <ChargesRow
                value={charges.outboundCharges}
                label="Outbound Charges"
                operator="plus"
                onInfoClick={() => toggleChargesInfoModal("outboundCharges")}
              />
            )}
            {!isSimpleCheckoutContractWarehousesMode && (
              <ChargesRow
                value={charges.laborCharges}
                label="Labor Charges"
                operator="plus"
                onInfoClick={() => toggleChargesInfoModal("laborCharges")}
              />
            )}
            {!isSimpleCheckoutContractWarehousesMode && (
              <ChargesRow
                value={charges.materialCharges}
                label="Material Charges"
                operator="plus"
                onInfoClick={() => toggleChargesInfoModal("materialCharges")}
              />
            )}
            {!isSimpleCheckoutContractWarehousesMode && (
              <ChargesRow
                value={charges.equipmentCharges}
                label="Equipment Charges"
                operator="plus"
                onInfoClick={() => toggleChargesInfoModal("equipmentCharges")}
              />
            )}
            <ChargesRow
              value={charges.oneTimeCharges}
              label="One-Time Flat Charges"
              operator="plus"
              onInfoClick={() => setOpenOneTime(true)}
            />
            <ChargesRow
              value={charges.salesTax}
              label="Sales Tax"
              operator="plus"
              showInfoButton={false}
            />
            <Divider sx={{ borderColor: "rgba(0, 0, 0, 0.5)" }} />
            <ChargesRow
              value={charges.billingCharges}
              label="Grand Total"
              operator="equals"
              showInfoButton={false}
            />
          </Box>
          {isInternalStaff && (
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button
                variant="outlined"
                color="primary"
                component={Link}
                to={getViewOrderDetailsTo()}
              >
                View Order Details
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>
      {openChargesInfoModal && (
        <CategoryChargesInfoModal
          onClose={() => toggleChargesInfoModal(null)}
          title={chargeTitles[openChargesInfoModal]}
          charges={inventoryRequest?.[openChargesInfoModal]}
          categoryKey={openChargesInfoModal}
          project={project}
          entity={inventoryRequest}
          onEdit={() => {
            if (openChargesInfoModal === "outboundCharges") {
              handleOpenBillingQuantities();
              return;
            }

            setOpenChargesEditModal(openChargesInfoModal);
            toggleChargesInfoModal(null);
          }}
        />
      )}
      {openChargesEditModal && (
        <InventoryRequestCategoryChargesEditModal
          open={true}
          onClose={() => {
            toggleChargesInfoModal(openChargesEditModal);
            setOpenChargesEditModal(null);
          }}
          title={chargeTitles[openChargesEditModal]}
          charges={inventoryRequest?.[openChargesEditModal]}
          project={project}
          inventoryRequest={inventoryRequest}
          categoryKey={openChargesEditModal}
        />
      )}
      {openOneTime && (
        <OneTimeChargesModal
          shipment={inventoryRequest}
          onClose={() => setOpenOneTime(false)}
          onSubmit={handleSubmit}
          loading={loading}
        />
      )}
      {openBillingQuantitiesModal && outboundBillingType && (
        <BillingQuantitiesModal
          type="Outbound"
          billingType={outboundBillingType}
          shipment={inventoryRequest}
          palletTypes={project?.palletTypes}
          unitTypes={project?.unitTypes}
          onSave={() => toggleBillingQuantitiesModal(false, "outboundCharges")}
          allowClear
          onClose={() => toggleBillingQuantitiesModal(false)}
        />
      )}
    </>
  );
}
